import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    LinearProgress,
    IconButton,
    TextField,
    Tabs,
    Tab,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import SearchIcon from '@mui/icons-material/Search';
import { InputAdornment } from '@mui/material';
import ScrollToTopButton from '../../Button/ScrollToTopButton';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate } from "react-router-dom";

const slideInFromLeft = keyframes`
  0% {
    transform: translateX(-20px);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const AnimatedTypography = styled(Typography)(({ theme }) => ({
    animation: `${slideInFromLeft} 0.5s ease-out`,
}));

const ExpandMore = styled((props) => {
    const { expand, ...other } = props;
    return <IconButton {...other} />;
})(({ theme, expand }) => ({
    transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
    }),
}));

const OrderTable = () => {
    const [loading, setLoading] = useState(false);
    const [productTotals, setProductTotals] = useState({});
    const [materialTotals, setMaterialTotals] = useState({});
    const [productImages, setProductImages] = useState({});
    const [expandedCard, setExpandedCard] = useState(null);
    const [user, setUser] = useState({});
    const [token, setToken] = useState(() => localStorage.getItem('token') || null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredProductTotals, setFilteredProductTotals] = useState({});
    const [filteredMaterialTotals, setFilteredMaterialTotals] = useState({});
    const [showSearch, setShowSearch] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const navigate = useNavigate();

    const handleSearchClick = () => {
        setShowSearch((prev) => !prev);
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setSearchTerm(''); // Clear search when switching tabs
    };

    useEffect(() => {
        if (token) {
            localStorage.setItem('token', token);
        }
    }, [token]);

    useEffect(() => {
        const fetchProfile = async () => {
            const token = localStorage.getItem("token");

            if (!token) {
                navigate("/");
                return;
            }

            try {
                const response = await axios.get("https://admin.bizstor.lk/public/api/profile", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setUser(response.data);
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem("token");
                    navigate("/");
                }
            }
        };

        fetchProfile();
    }, []);

    useEffect(() => {
        if (user.id) {
            fetchData();
            fetchDamageData();
            fetchOrderData();
            fetchStockData();
            fetchDamageData();
        }
    }, [user.id]);

    const fetchOrderData = async () => {
        try {
            const response = await axios.get('https://admin.bizstor.lk/public/api/get-data');
            const userId = user.id;

            let Orders = [];

            if (userId) {
                Orders = response.data.filter(order =>
                    order.usertoken.toLowerCase() === userId.toString().toLowerCase()
                );
            } else {
                Orders = response.data;
            }

            const productTotals = {};
            const materialTotals = {};

            Orders.forEach(order => {
                order.items.forEach(item => {
                    const productName = item.product || 'Unknown Product';
                    
                    // Debug logging - remove this later
                    console.log('Order item:', { product: productName, status: item.status, quantity: item.quantity });
                    
                    if (item.status === 'Item' || item.status === 'item') {
                        if (!productTotals[productName]) {
                            productTotals[productName] = { sellTotal: 0, filledTotal: 0, emptyTotal: 0, damageTotal: 0, itemTotal: 0 };
                        }
                        productTotals[productName].sellTotal += Number(item.quantity);
                    } else if (item.status === 'Empty' || item.status === 'Filled' || item.status === 'empty' || item.status === 'filled') {
                        if (!materialTotals[productName]) {
                            materialTotals[productName] = { sellTotal: 0, filledTotal: 0, emptyTotal: 0, damageTotal: 0, itemTotal: 0 };
                        }
                        materialTotals[productName].sellTotal += Number(item.quantity);
                    } else {
                        // For any other status or undefined status, default to product
                        if (!productTotals[productName]) {
                            productTotals[productName] = { sellTotal: 0, filledTotal: 0, emptyTotal: 0, damageTotal: 0, itemTotal: 0 };
                        }
                        productTotals[productName].sellTotal += Number(item.quantity);
                    }
                });
            });

            console.log('Product totals:', productTotals);
            console.log('Material totals:', materialTotals);

            return { productTotals, materialTotals };
        } catch (error) {
            console.error('Error fetching order data:', error);
            return { productTotals: {}, materialTotals: {} };
        }
    };

    const fetchStockData = async (existingTotals) => {
        try {
            const response = await axios.get('https://admin.bizstor.lk/public/api/get-stock-data');
            const userId = user.id;

            let Orders = [];

            if (userId) {
                Orders = response.data.filter(stock =>
                    stock.usertoken.toLowerCase() === userId.toString().toLowerCase()
                );
            } else {
                Orders = response.data;
            }

            const productTotals = { ...existingTotals.productTotals };
            const materialTotals = { ...existingTotals.materialTotals };

            Orders.forEach(stock => {
                stock.items.forEach(item => {
                    const productName = item.product ? item.product : 'Unknown Product';
                    
                    // Debug logging
                    console.log('Stock item:', { product: productName, status: item.status, quantity: item.quantity });
                    
                    if (item.status === 'Item' || item.status === 'item') {
                        if (!productTotals[productName]) {
                            productTotals[productName] = { sellTotal: 0, filledTotal: 0, emptyTotal: 0, damageTotal: 0, itemTotal: 0 };
                        }
                        productTotals[productName].itemTotal += Number(item.quantity);
                    } else if (item.status === 'Empty' || item.status === 'Filled' || item.status === 'empty' || item.status === 'filled') {
                        if (!materialTotals[productName]) {
                            materialTotals[productName] = { sellTotal: 0, filledTotal: 0, emptyTotal: 0, damageTotal: 0, itemTotal: 0 };
                        }
                        if (item.status === 'Filled' || item.status === 'filled') {
                            materialTotals[productName].filledTotal += Number(item.quantity);
                        } else if (item.status === 'Empty' || item.status === 'empty') {
                            materialTotals[productName].emptyTotal += Number(item.quantity);
                        }
                    }
                });
            });

            return { productTotals, materialTotals };
        } catch (error) {
            console.error('Error fetching stock data:', error);
            return existingTotals;
        }
    };

    const fetchDamageData = async (existingTotals) => {
        try {
            const response = await axios.get('https://admin.bizstor.lk/public/api/get-damage-data');
            const userId = user.id;

            let Orders = [];

            if (userId) {
                Orders = response.data.filter(damage =>
                    damage.usertoken.toLowerCase() === userId.toString().toLowerCase()
                );
            } else {
                Orders = response.data;
            }

            const productTotals = { ...existingTotals.productTotals };
            const materialTotals = { ...existingTotals.materialTotals };

            Orders.forEach(order => {
                order.items.forEach(item => {
                    const productName = item.product ? item.product : 'Unknown Product';
                    
                    // Debug logging
                    console.log('Damage item:', { product: productName, status: item.status, quantity: item.quantity, type: order.type });
                    
                    if (item.status === 'Item' || item.status === 'item') {
                        if (!productTotals[productName]) {
                            productTotals[productName] = { sellTotal: 0, filledTotal: 0, emptyTotal: 0, damageTotal: 0, itemTotal: 0 };
                        }
                        if (order.type === 'replace') {
                            productTotals[productName].sellTotal += Number(item.quantity);
                            productTotals[productName].damageTotal += Number(item.quantity);
                        } else {
                            productTotals[productName].damageTotal += Number(item.quantity);
                        }
                    } else if (item.status === 'Empty' || item.status === 'Filled' || item.status === 'empty' || item.status === 'filled') {
                        if (!materialTotals[productName]) {
                            materialTotals[productName] = { sellTotal: 0, filledTotal: 0, emptyTotal: 0, damageTotal: 0, itemTotal: 0 };
                        }
                        if (order.type === 'replace') {
                            materialTotals[productName].sellTotal += Number(item.quantity);
                            materialTotals[productName].damageTotal += Number(item.quantity);
                        } else {
                            materialTotals[productName].damageTotal += Number(item.quantity);
                        }
                    }
                });
            });

            return { productTotals, materialTotals };
        } catch (error) {
            console.error('Error fetching damage data:', error);
            return existingTotals;
        }
    };

    const fetchProductImages = async () => {
        try {
            const response = await axios.get('https://admin.bizstor.lk/public/api/get-product-data');
            const data = response.data.products;

            const productData = {};

            data.forEach(product => {
                if (product.product) {
                    productData[product.product] = {
                        image: product.image
                            ? `https://admin.bizstor.lk/storage/app/public/${product.image}`
                            : null,
                        code: product.code || null
                    };
                }
            });

            setProductImages(productData);
        } catch (error) {
            console.error('Error fetching product data:', error);
        }
    };

    const fetchData = async () => {
        setLoading(true);

        try {
            const orderTotals = await fetchOrderData();
            const stockTotals = await fetchStockData(orderTotals);
            const damageTotals = await fetchDamageData(stockTotals);

            setProductTotals(damageTotals.productTotals);
            setMaterialTotals(damageTotals.materialTotals);

            await fetchProductImages();

        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 1000);
        }
    };

    const filterData = (term) => {
        // Filter product data
        const filteredProducts = Object.keys(productTotals)
            .filter(product => {
                const productNameMatches = product.toLowerCase().includes(term.toLowerCase());
                const productCodeMatches = productImages[product]?.code?.toLowerCase().includes(term.toLowerCase());
                return productNameMatches || productCodeMatches;
            })
            .reduce((acc, key) => {
                acc[key] = productTotals[key];
                return acc;
            }, {});

        // Filter material data
        const filteredMaterials = Object.keys(materialTotals)
            .filter(product => {
                const productNameMatches = product.toLowerCase().includes(term.toLowerCase());
                const productCodeMatches = productImages[product]?.code?.toLowerCase().includes(term.toLowerCase());
                return productNameMatches || productCodeMatches;
            })
            .reduce((acc, key) => {
                acc[key] = materialTotals[key];
                return acc;
            }, {});

        setFilteredProductTotals(filteredProducts);
        setFilteredMaterialTotals(filteredMaterials);
    };

    useEffect(() => {
        filterData(searchTerm);
    }, [productTotals, materialTotals, searchTerm]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
    };

    const renderInventoryCards = (totals) => {
        return Object.keys(totals).map(product => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={product}>
                <Card
                    sx={{
                        width: '100%',
                        borderRadius: '12px',
                        mb: 2,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        transition: 'transform 0.3s, box-shadow 0.3s',
                        '&:hover': {
                            transform: 'scale(1.03)',
                            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.2)',
                        },
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%'
                    }}
                >
                    <CardContent
                        sx={{
                            flexGrow: 1,
                            p: 2,
                            backgroundColor: '#f9f9f9',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'flex-start',
                            gap: 2
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <Box
                                component="img"
                                sx={{
                                    height: 80,
                                    width: 80,
                                    borderRadius: '8px',
                                    objectFit: 'cover',
                                    flexShrink: 0
                                }}
                                src={productImages[product]?.image || "https://via.placeholder.com/80"}
                                alt={product}
                            />
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: '0.75rem',
                                    color: 'text.secondary',
                                    textAlign: 'center',
                                    maxWidth: 80,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                {productImages[product]?.code || 'No Code'}
                            </Typography>
                        </Box>

                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1
                        }}>
                            <Box sx={{
                                mb: 1,
                                pb: 1,
                            }}>
                                <Typography variant="body1" sx={{
                                    fontWeight: 'bold',
                                    fontSize: '1rem',
                                    color: 'text.primary'
                                }}>
                                    {product}
                                </Typography>
                            </Box>

                            <Typography variant="body2" sx={{
                                fontSize: '0.8rem',
                                color: (totals[product]?.emptyTotal || totals[product]?.itemTotal > 0) ? 'success.main' : 'error.main',
                                mb: 1.5
                            }}>
                                {totals[product]?.emptyTotal ?
                                    `In Stock: ${totals[product].emptyTotal - (totals[product]?.filledTotal || 0)}` :
                                    totals[product]?.itemTotal != null && totals[product]?.itemTotal > 0 ?
                                        `In Stock: ${totals[product].itemTotal - (totals[product]?.sellTotal || 0)}` :
                                        'Out of Stock'}
                            </Typography>

                            <Box sx={{
                                mt: 'auto',
                                pt: 1
                            }}>
                                {totals[product] ? (
                                    <>
                                        {(totals[product].filledTotal != null && totals[product].filledTotal > 0) ||
                                            (totals[product].emptyTotal != null && totals[product].emptyTotal > 0) ? (
                                            <>
                                                {totals[product].filledTotal != null && totals[product].filledTotal > 0 && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                            Used:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                            {totals[product].filledTotal - totals[product].sellTotal || 0}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {totals[product].emptyTotal != null && totals[product].emptyTotal > 0 && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                            In Stock:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                            {totals[product].emptyTotal - totals[product].filledTotal || 0}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                        Sold:
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                        {totals[product].sellTotal}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                        Returns:
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                        {totals[product].damageTotal}
                                                    </Typography>
                                                </Box>
                                            </>
                                        )}
                                    </>
                                ) : null}

                                {totals[product].emptyTotal ? (
                                    <Box sx={{ width: '100%', mt: 1.5 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={
                                                totals[product]?.emptyTotal > 0 ?
                                                    (totals[product].filledTotal / totals[product].emptyTotal) * 100 :
                                                    100
                                            }
                                            sx={{
                                                height: 6,
                                                borderRadius: 3,
                                                '& .MuiLinearProgress-bar': {
                                                    backgroundColor: 'orange',
                                                },
                                            }}
                                        />
                                        <Typography variant="caption" sx={{
                                            display: 'block',
                                            textAlign: 'right',
                                            fontSize: '0.65rem',
                                            mt: 0.5,
                                            color: 'text.secondary'
                                        }}>
                                            {totals[product]?.emptyTotal > 0 ?
                                                `${Math.round((totals[product].filledTotal / totals[product].emptyTotal) * 100)}% Used` :
                                                '100% (out of stock)'
                                            }
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box sx={{ width: '100%', mt: 1.5 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={
                                                totals[product]?.itemTotal > 0 ?
                                                    (totals[product].sellTotal / totals[product].itemTotal) * 100 :
                                                    100
                                            }
                                            sx={{
                                                height: 6,
                                                borderRadius: 3,
                                                '& .MuiLinearProgress-bar': {
                                                    backgroundColor: 'orange',
                                                },
                                            }}
                                        />
                                        <Typography variant="caption" sx={{
                                            display: 'block',
                                            textAlign: 'right',
                                            fontSize: '0.65rem',
                                            mt: 0.5,
                                            color: 'text.secondary'
                                        }}>
                                            {totals[product]?.itemTotal > 0 ?
                                                `${Math.round((totals[product].sellTotal / totals[product].itemTotal) * 100)}% sold` :
                                                '100% sold (out of stock)'
                                            }
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
        ));
    };

    return (
        <Box sx={{mt:10}}>
            <AnimatedTypography
                component="h1"
                sx={{
                    ml: { xs: 2, sm: 3 },
                    mt: { xs: 2, sm: 3 },
                    color: '',
                    letterSpacing: '0.01em',
                    fontSize: { xs: '20px', sm: '24px' },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center',ml:1 }}>
                    <HomeIcon
                        sx={{
                            ml: 1,
                            fontSize: '18px',
                            color: '#6d7081',
                        }}
                    />
                    <Typography
                        component="span"
                        sx={{
                            ml: 0.5,
                            fontSize: '14px',
                            color: '#6d7081',
                        }}
                    >
                        - Inventory Details
                    </Typography>
                </Box>
            </AnimatedTypography>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: {sm:'95%',xs:'90%'},
                    maxWidth: 'auto',
                    margin: 'auto',
                    marginBottom: '30px',
                    bgcolor: 'background.paper',
                    border: '1px solid #EDF2F7',
                    borderRadius: '16px',
                    padding: '10px',
                    marginTop: '30px',
                    position: 'relative'
                }}
            >
                {loading && (
                    <Box sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'white',
                        zIndex: 9999
                    }}>
                        <l-trio
                            size="50"
                            speed="1.3"
                            color="red"
                        ></l-trio>
                    </Box>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        mb: 2
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            '@media (max-width: 600px)': {
                                margin: 'auto',
                            },
                            color: '#1e293b',
                            ml: 2
                        }}
                    >
                        <h2>Inventory Details</h2>
                    </Box>
                    <Box sx={{
                            '@media (max-width: 600px)': {
                                display: 'flex',
                                justifyContent: 'center',
                                width: '100%',
                            },
                        }}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            size="small"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            placeholder="Search…"
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                marginBottom: '20px', width: '250px', mr: 2,
                                borderRadius: '8px',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                },
                                '@media (max-width: 600px)': {
                                    margin: 'auto',
                                    marginBottom: '20px',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                },
                            }}
                        />
                    </Box>
                </Box>

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', ml: 2, mr: 2 }}>
                    <Tabs value={activeTab} onChange={handleTabChange} aria-label="inventory tabs">
                        <Tab label="Product" />
                        <Tab label="Material" />
                    </Tabs>
                </Box>

                {/* Tab Content */}
                <Box sx={{ mt: 2 }}>
                    {activeTab === 0 && (
                        <Grid container spacing={3}>
                            {renderInventoryCards(filteredProductTotals)}
                        </Grid>
                    )}
                    {activeTab === 1 && (
                        <Grid container spacing={3}>
                            {renderInventoryCards(filteredMaterialTotals)}
                        </Grid>
                    )}
                </Box>
            </Box>
            <ScrollToTopButton />
        </Box>
    );
};

export default OrderTable;